/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import { applyParameters } from '@modules/query/helpers'

/**
 * Hooks for proxy requests to projects' underlying Elasticsearch instance.
 *
 * Due to the fact that these payloads (both request and response) are so
 * open-ended, it is much easier to place the fetch calls manually rather than
 * use our generated caller functions and react-query scaffolding and helpers.
 */

type ProjectProxyRequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

export const useProxyMutation = <TProject extends ProjectType>(
  projectType: TProject,
  projectId: ProjectID,
) =>
  useMutation<
    Response,
    unknown,
    {
      method: ProjectProxyRequestMethod
      path: string
      body: string
    }
  >({
    mutationFn: ({ method, path, body }) => {
      const pathParameters: { id: string; path: string } = {
        id: projectId,
        path,
      }

      const processedBody = processBody(body)

      const headers = { 'Content-Type': 'application/json' }

      // work around fetch's prohibition on GET requests with payloads
      const isGetWithBody = Boolean(method === `GET` && body.length)
      const requestMethod = isGetWithBody ? `POST` : method

      if (isGetWithBody) {
        headers['X-HTTP-Method-Override'] = 'GET'
      }

      return fetch(
        applyParameters(`/api/v1/admin/serverless/projects/${projectType}/{id}/_proxy/{path}`, {
          pathParameters,
        }),
        {
          method: requestMethod,
          body: processedBody,
          headers,
        },
      )
    },
  })

function processBody(body: string): string | undefined {
  return body.length ? body : undefined
}
